import {
  computeImpliedProfitFromSellingPrice,
  computePriceBeforeProfitLayer,
  roundMoney,
} from './budgetEngineV2';

const nf = (v) => {
  const n = typeof v === 'number' ? v : parseFloat(v);
  return Number.isFinite(n) ? n : 0;
};

const ni = (v) => {
  const n = typeof v === 'number' ? v : parseInt(v, 10);
  return Number.isFinite(n) ? n : 0;
};

export function machineHourCostFromConstants(constants) {
  const maintenanceHour = nf(constants.machine?.equipment_maintenance_per_hour);
  const degradationHour = nf(constants.machine?.equipment_degradation_per_hour);
  const energyHour = nf(constants.machine?.energy_per_hour);
  const machineHourCost = roundMoney(maintenanceHour + degradationHour + energyHour);
  return {
    maintenanceHour,
    degradationHour,
    energyHour,
    machineHourCost,
  };
}

export function legacyProductToBudgetParts(constants, productInput) {
  const q = Math.max(1, parseInt(String(productInput.quantity), 10) || 1);
  const mat = constants.materials[productInput.material];
  const filament = roundMoney((nf(productInput.grams) / 1000) * nf(mat?.cost_per_kg));
  const printHours = nf(productInput.print_hours);
  const { machineHourCost, maintenanceHour, degradationHour, energyHour } =
    machineHourCostFromConstants(constants);
  const printingMachine = roundMoney(printHours * machineHourCost);

  const directLineAmounts = [
    { id: 'filament', label: 'filament', amount: filament },
    { id: 'printing_machine', label: 'printing_machine', amount: printingMachine },
  ];

  const variableCostLines = [];
  const vcList = Array.isArray(productInput.variable_costs) ? productInput.variable_costs : [];
  for (const row of vcList) {
    const name = String(row?.name || '').trim();
    const cu = nf(row?.cost_unit);
    if (!name && cu <= 0) continue;
    const ct = roundMoney(cu * q);
    const cht = 0;
    if (ct > 0) {
      directLineAmounts.push({
        id: `var-cost-${variableCostLines.length}`,
        label: name || '—',
        amount: ct,
      });
    }
    variableCostLines.push({
      name: name || '—',
      cost_total: ct,
      charged_total: cht,
    });
  }

  const revenueModeling = roundMoney(
    nf(productInput.modeling_hours) * nf(constants.services?.modeling_per_hour),
  );
  const revenuePainting = roundMoney(
    nf(productInput.painting_hours) * nf(constants.services?.painting_per_hour),
  );
  const revenuePrintMonitoring = roundMoney(
    printHours * nf(constants.machine?.print_labor_per_hour),
  );

  const billingLineAmounts = [
    { id: 'modeling', amount: revenueModeling },
    { id: 'painting', amount: revenuePainting },
    { id: 'print_monitoring', amount: revenuePrintMonitoring },
  ];

  const chargedLineAmounts = [];

  const feeLayers = [
    { id: 'marketing', mode: 'inclusive', percent: constants.percentages?.marketing_percent },
    { id: 'reinvestment', mode: 'on_top', percent: constants.pricing?.reinvestment_percent },
    { id: 'profit_margin', mode: 'on_top', percent: constants.pricing?.profit_margin_percent },
  ];

  const failurePercent = constants.percentages?.failure_percent;

  return {
    quantity: q,
    directLineAmounts,
    failurePercent,
    billingLineAmounts,
    chargedLineAmounts,
    feeLayers,
    legacyDetail: {
      filament,
      printingMachine,
      variableCostLines,
      revenueModeling,
      revenuePainting,
      revenuePrintMonitoring,
      marketingPercent: constants.percentages?.marketing_percent,
      reinvestmentPercent: constants.pricing?.reinvestment_percent,
      profitMarginPercent: constants.pricing?.profit_margin_percent,
      printHours,
      grams: nf(productInput.grams),
      materialCostPerKg: nf(mat?.cost_per_kg),
      machineHourBreakdown: {
        maintenance_per_hour: maintenanceHour,
        degradation_per_hour: degradationHour,
        energy_per_hour: energyHour,
        hour_cost_sum: machineHourCost,
      },
      failurePercent,
    },
  };
}

export function resolveSellingPriceTotal(productInput) {
  const total = nf(productInput?.selling_price_total);
  if (total > 0) return roundMoney(total);
  return null;
}

export function applySellingPriceToLegacySimulationData(data, parts, sellingPriceTotal) {
  if (!data || sellingPriceTotal == null || sellingPriceTotal <= 0) {
    return data;
  }

  const q = Math.max(1, ni(data.detail?.quantity));
  const minimumSellingPrice = nf(data.pricing.minimum_selling_price);
  const priceAfterReinvest = computePriceBeforeProfitLayer(
    data.composition.subtotal_before_marketing,
    parts.feeLayers,
  );
  const implied = computeImpliedProfitFromSellingPrice(priceAfterReinvest, sellingPriceTotal);
  const effectiveSellingPrice = roundMoney(sellingPriceTotal);
  const effectiveUnitPrice = roundMoney(effectiveSellingPrice / q);
  const profitOnDirectCost = roundMoney(effectiveSellingPrice - nf(data.costs.total_direct_cost));
  let marginPctOnFinal = 0;
  if (effectiveSellingPrice > 0) {
    marginPctOnFinal = roundMoney((profitOnDirectCost / effectiveSellingPrice) * 100);
  }

  return {
    ...data,
    profit_margin: {
      ...data.profit_margin,
      percent: implied.percent,
      amount: implied.amount,
      price_before: priceAfterReinvest,
      price_after: effectiveSellingPrice,
      is_implied: true,
      configured_percent: data.profit_margin.percent,
    },
    pricing: {
      ...data.pricing,
      minimum_selling_price: minimumSellingPrice,
      minimum_unit_price: roundMoney(minimumSellingPrice / q),
      selling_price: effectiveSellingPrice,
      effective_selling_price: effectiveSellingPrice,
      final_price: effectiveSellingPrice,
      unit_price: effectiveUnitPrice,
      profit_on_direct_cost: profitOnDirectCost,
      margin_pct_on_final: marginPctOnFinal,
    },
  };
}

export function mapBudgetResultToLegacySimulationData(parts, engineData, options = {}) {
  const d = engineData;
  const L = parts.legacyDetail;
  const variable_costs = L.variableCostLines;

  const trace = d.layerTrace || [];
  const marketingLayer = trace.find((t) => t.id === 'marketing');
  const reinvestLayer = trace.find((t) => t.id === 'reinvestment');
  const profitLayer = trace.find((t) => t.id === 'profit_margin');

  const priceAfterMarketing = marketingLayer ? marketingLayer.amountAfter : d.subtotalBeforeFees;

  const reinvestmentAmount = reinvestLayer
    ? roundMoney(reinvestLayer.amountAfter - reinvestLayer.amountBefore)
    : 0;

  const profitMarginAmount = profitLayer
    ? roundMoney(profitLayer.amountAfter - profitLayer.amountBefore)
    : 0;

  const minimumSellingPrice = d.finalPrice;
  const minimumUnitPrice = d.unitPrice;

  const baseResult = {
    costs: {
      filament: L.filament,
      printing_machine: L.printingMachine,
      variable_costs,
      subtotal_direct: d.directBase,
      failure_cost: d.failureCost,
      total_direct_cost: d.totalDirect,
    },
    services_billing: {
      modeling: L.revenueModeling,
      painting: L.revenuePainting,
      print_monitoring: L.revenuePrintMonitoring,
      total: d.billingTotal,
    },
    composition: {
      base_from_direct_cost: d.totalDirect,
      variable_charged_total: d.chargedTotal,
      subtotal_before_marketing: d.subtotalBeforeFees,
    },
    fees: {
      marketing_percent: L.marketingPercent,
      price_after_marketing: priceAfterMarketing,
    },
    reinvestment: {
      percent: L.reinvestmentPercent,
      amount: reinvestmentAmount,
      price_before: reinvestLayer ? reinvestLayer.amountBefore : priceAfterMarketing,
      price_after: reinvestLayer ? reinvestLayer.amountAfter : priceAfterMarketing,
    },
    profit_margin: {
      percent: L.profitMarginPercent,
      amount: profitMarginAmount,
      price_before: profitLayer ? profitLayer.amountBefore : d.finalPrice,
      price_after: profitLayer ? profitLayer.amountAfter : d.finalPrice,
      is_implied: false,
      configured_percent: L.profitMarginPercent,
    },
    detail: {
      quantity: d.quantity,
      print_hours: L.printHours,
      grams: L.grams,
      material_cost_per_kg: L.materialCostPerKg,
      machine_hour: L.machineHourBreakdown,
      failure_percent: L.failurePercent,
    },
    pricing: {
      subtotal_before_marketing: d.subtotalBeforeFees,
      minimum_selling_price: minimumSellingPrice,
      minimum_unit_price: minimumUnitPrice,
      selling_price: null,
      effective_selling_price: minimumSellingPrice,
      final_price: minimumSellingPrice,
      unit_price: minimumUnitPrice,
      profit_on_direct_cost: d.profitOnDirectCost,
      effective_markup_on_direct: d.effectiveMarkupOnDirect,
      margin_pct_on_final: d.marginPctOnFinal,
    },
  };

  const sellingPriceTotal = options.sellingPriceTotal;
  if (sellingPriceTotal != null && sellingPriceTotal > 0) {
    return applySellingPriceToLegacySimulationData(baseResult, parts, sellingPriceTotal);
  }

  return baseResult;
}
